import { action }                              from 'mobx';
import { computed }                            from 'mobx';
import { makeObservable }                      from 'mobx';
import { observable }                          from 'mobx';
import { reaction }                            from 'mobx';
import { runInAction }                         from 'mobx';
import ApiCollection                           from '@widesk-core/models/ApiCollection';
import PagedCollection                         from '@widesk-core/models/PagedCollection';
import dayjs                                   from 'dayjs';
import AccountBillingProfileModel              from '@models/account/AccountBillingProfileModel';
import AccountSubscriptionModel                from '@models/account/AccountSubscriptionModel';
import AccountSubscriptionPriceModel           from '@models/account/AccountSubscriptionPriceModel';
import InvoiceModel                            from '@models/account/InvoiceModel';
import RequestChangeSubscriptionModel          from '@models/account/RequestChangeSubscriptionModel';
import { OPEN_REQUEST_STATUS_IDS }             from '@models/account/RequestChangeSubscriptionModel';
import { RequestChangeSubscriptionStatusId }   from '@models/account/RequestChangeSubscriptionModel';
import SubscriptionModel                       from '@models/account/SubscriptionModel';
import SubscriptionPeriodModel                 from '@models/account/SubscriptionPeriodModel';
import SubscriptionPriceModel                  from '@models/account/SubscriptionPriceModel';
import { functionalityStore }                  from '@/components/Functionality';
import { toLimit }                             from '@/components/Functionality/engine/types';
import { FunctionalityValue }                  from '@/components/Functionality';
import { LIMIT_SCOPES_ORDER }                  from '@/helpers/subscription/functionalityLabels';
import { FUNCTIONALITY_THEMES }                from '@/helpers/subscription/functionalityThemes';
import { OTHER_FUNCTIONALITIES_TITLE }         from '@/helpers/subscription/functionalityThemes';
import { acknowledgeClosedChange }             from '@/helpers/subscription/closedChangeStorage';
import { isClosedChangeAcknowledged }          from '@/helpers/subscription/closedChangeStorage';
import { requestSubscriptionCancellation }      from '@/helpers/subscription/planChange';
import { isFreePlan }                          from '@/helpers/subscription/plans';
import { isOnDemandPlan }                      from '@/helpers/subscription/plans';
import { subscriptionTerm }                    from '@/helpers/subscription/subscriptionTerm';
import appStore                                from '@/stores/appStore';

/** Au-delà, une demande fermée par le back n'intéresse plus personne : le client a reçu l'e-mail. */
const ANNOUNCE_CLOSED_FOR_DAYS = 7;

/** Ce qu'une offre comprend, fonctionnalité par fonctionnalité, au regard de l'abonnement en cours. */
export type PlanFunctionalityState = 'gained' | 'included' | 'missing' | 'reduced';

export const functionalityState = (value?: FunctionalityValue, currentValue?: FunctionalityValue): PlanFunctionalityState => {
	if (!value || !functionalityStore.isIncluded(value.reference, value.value)) return 'missing';

	const wasIncluded = !!currentValue && functionalityStore.isIncluded(currentValue.reference, currentValue.value);

	if (functionalityStore.isFeature(value.reference)) return wasIncluded ? 'included' : 'gained';

	const from = wasIncluded ? toLimit(currentValue?.value) : 0;
	const to = toLimit(value.value);

	if (to > from) return 'gained';
	if (to < from) return 'reduced';

	return 'included';
};

/** Les limites du compte d'abord, puis les périmètres plus fins, et enfin les fonctionnalités. */
const sortForDisplay = (values: FunctionalityValue[]) => {
	const limits = values.filter(value => !functionalityStore.isFeature(value.reference));
	const scopeOf = (value: FunctionalityValue) => functionalityStore.scopeOf(value.reference) || '';

	return [
		...limits.filter(value => !LIMIT_SCOPES_ORDER.includes(scopeOf(value))),
		...LIMIT_SCOPES_ORDER.flatMap(scope => limits.filter(value => scopeOf(value) === scope)),
		...values.filter(value => functionalityStore.isFeature(value.reference)),
	];
};

export type EffectivePrice = {
	amountExcludingVat: number;
	currency: string;
	isNegotiated: boolean;
};

/**
 * Tout ce que le studio sait de l'abonnement du compte courant : son contrat, le catalogue des
 * plans, et les demandes de changement en cours ou récemment fermées.
 *
 * Les écrans le lisent et l'appellent, mais ne chargent rien eux-mêmes : une seule requête par
 * donnée, et un seul `refresh()` après une action — impossible d'oublier de recharger l'une des
 * listes, ce qui laissait auparavant des bandeaux périmés à l'écran.
 */
export class SubscriptionStore {
	private readonly _accountSubscriptions = new ApiCollection(AccountSubscriptionModel);
	private readonly _billingProfiles = new ApiCollection(AccountBillingProfileModel);
	private readonly _openChanges = new ApiCollection(RequestChangeSubscriptionModel);
	private readonly _closedChanges = new PagedCollection(RequestChangeSubscriptionModel);
	private readonly _plans = new ApiCollection(SubscriptionModel);
	private readonly _periods = new ApiCollection(SubscriptionPeriodModel);
	private readonly _catalogPrices = new ApiCollection(SubscriptionPriceModel);
	private readonly _negotiatedPrices = new ApiCollection(AccountSubscriptionPriceModel);
	private readonly _invoices = new PagedCollection(InvoiceModel);

	/** Compte pour lequel le contrat a été chargé : un autre compte repart de zéro. */
	@observable private _contractLoadedFor: Urn | '' = '';

	@observable private _isContractLoaded = false;

	@observable private _isCatalogueLoaded = false;

	@observable private _isPriceLoaded = false;

	@observable.ref private _effectivePrice: EffectivePrice | undefined;

	/** Périodicité regardée dans la grille des plans. */
	@observable private _periodName: Account_SubscriptionPeriodName = 'MONTHLY';

	/** Le client a choisi lui-même une périodicité : ne plus la réécrire à chaque rechargement. */
	@observable private _hasPickedPeriod = false;

	/** Demande fermée que le client vient de lire : inutile de la lui remontrer avant rechargement. */
	@observable private _acknowledgedClosedChangeId: id | undefined;

	constructor() {
		makeObservable(this);

		// Changer de compte change tout : on oublie ce qu'on savait, le prochain écran rechargera.
		reaction(() => appStore.accountUrn, () => this.reset());
	}

	// ————— Chargement —————

	/** À appeler au montage des écrans d'abonnement. Ne recharge pas ce qui l'est déjà. */
	public async loadContract() {
		const accountUrn = appStore.accountUrn;

		if (!accountUrn || this._contractLoadedFor === accountUrn) return;

		runInAction(() => this._contractLoadedFor = accountUrn);

		try {
			await this.fetchContract();
		} catch (error) {
			// Le chargement a échoué (le connecteur l'a déjà signalé au client) : on relâche la garde
			// pour que le prochain passage sur un écran d'abonnement retente.
			runInAction(() => this._contractLoadedFor = '');
		}
	}

	/** La liste des plans seule, pour les écrans qui n'ont pas besoin des tarifs. */
	public async loadPlans() {
		if (this._plans.isLoaded || this._plans.isLoading) return;

		await this._plans
			.setRequiredFilter('subscriptionStatus.reference', 'ENABLED')
			.setSort('position', 'asc')
			.list();
	}

	/** Le catalogue ne dépend pas du compte, sauf pour ses tarifs négociés. */
	public async loadCatalogue() {
		functionalityStore.loadAccountUsages();

		await this.loadCatalogueOnce();
	}

	private async loadCatalogueOnce() {
		if (this._isCatalogueLoaded) return;

		// Les valeurs des plans viennent du bundle : c'est lui qui sait ce que chaque plan comprend.
		functionalityStore.loadPlanValues();

		await Promise.all([
			this.loadPlans(),
			this._periods.list(),
			this._catalogPrices.list(),
			this.fetchNegotiatedPrices(),
		]);

		runInAction(() => this._isCatalogueLoaded = true);
	}

	public async loadUpgradeOptions() {
		await Promise.all([
			this.loadContract(),
			this.loadCatalogueOnce(),
		]);
	}

	/** Après une action sur le contrat : l'abonnement, les demandes et les valeurs ont pu changer. */
	public async refresh() {
		await Promise.all([
			this.fetchContract(),
			this.fetchNegotiatedPrices(),
		]);
	}

	private async fetchContract() {
		const accountUrn = appStore.accountUrn;
		const accountId = appStore.account?.id;

		if (!accountUrn || !accountId) return;

		await Promise.all([
			this.fetchAccountSubscription(accountUrn),
			this._billingProfiles.setRequiredFilter('account', accountId).list(),
		]);

		await Promise.all([
			this.fetchChanges(),
			this.fetchEffectivePrice(),
			this.fetchInvoicePresence(accountId),
		]);

		// Le compte a pu changer pendant ces requêtes : leur réponse ne parle plus de ce qui est
		// affiché, et le rechargement déclenché par le changement de compte fera le travail.
		if (appStore.accountUrn !== accountUrn) return;

		const accountSubscriptionId = this.accountSubscription?.id;

		if (accountSubscriptionId) functionalityStore.refreshAccountValues(accountSubscriptionId);

		// Les comptages ne servent qu'à comparer des plans : inutile de les relever ailleurs.
		if (this._isCatalogueLoaded) functionalityStore.loadAccountUsages();

		runInAction(() => this._isContractLoaded = true);
	}

	private async fetchAccountSubscription(accountUrn: Urn) {
		await this._accountSubscriptions.setRequiredFilter('account', accountUrn).list();
		await this._accountSubscriptions.whenIsLoaded(m => [m.subscription, m.accountSubscriptionStatus]);

		const accountSubscription = this._accountSubscriptions.findByResolvableUrn('account', accountUrn);

		if (accountSubscription?.subscriptionPeriod?.id) {
			await accountSubscription.subscriptionPeriod.fetch();
		}

		const periodName = accountSubscription?.subscriptionPeriod?.name;

		// La grille s'ouvre sur la périodicité du compte, mais ne contredit pas un choix du client.
		if (periodName && !this._hasPickedPeriod) runInAction(() => this._periodName = periodName);
	}

	private async fetchChanges() {
		const accountSubscriptionId = this.accountSubscription?.id;

		if (!accountSubscriptionId) return;

		await Promise.all([
			this._openChanges
				.setRequiredFilter('accountSubscription', accountSubscriptionId)
				.setRequiredFilter('requestChangeSubscriptionStatus', OPEN_REQUEST_STATUS_IDS)
				.list(),
			this._closedChanges
				.setItemsPerPage(1)
				.setRequiredFilter('accountSubscription', accountSubscriptionId)
				.setRequiredFilter('requestChangeSubscriptionStatus', RequestChangeSubscriptionStatusId.cancelled)
				.setSort('id', 'desc')
				.list(),
		]);

		await Promise.all([
			this._openChanges.whenIsLoaded(m => [m.toSubscription, m.fromSubscription]),
			this._closedChanges.whenIsLoaded(m => m.toSubscription),
		]);
	}

	private async fetchInvoicePresence(accountId: id) {
		if (!isFreePlan(this.accountSubscription?.subscription)) return;

		await this._invoices
			.setItemsPerPage(1)
			.setRequiredFilter('accountSubscription.account', accountId)
			.list();
	}

	private async fetchNegotiatedPrices() {
		const accountId = appStore.account?.id;

		if (!accountId) return;

		await this._negotiatedPrices.setRequiredFilter('account', accountId).list();
	}

	/**
	 * Le prix réellement facturé : celui négocié pour le compte s'il en a un, sinon le tarif
	 * catalogue de son plan et de sa périodicité.
	 */
	private async fetchEffectivePrice() {
		const accountSubscription = this.accountSubscription;
		const negotiated = accountSubscription?.accountSubscriptionPrice;

		const apply = (price?: { amountExcludingVat: number; currency: string }, isNegotiated = false) => {
			runInAction(() => {
				this._effectivePrice = price
					? { amountExcludingVat: price.amountExcludingVat, currency: price.currency, isNegotiated }
					: undefined;
				this._isPriceLoaded = true;
			});
		};

		if (negotiated?.id) {
			await negotiated.fetch();
			apply(negotiated, true);
			return;
		}

		const subscriptionId = accountSubscription?.subscription?.id;
		const periodId = accountSubscription?.subscriptionPeriod?.id;

		if (!subscriptionId || !periodId) {
			apply(undefined);
			return;
		}

		const prices = new ApiCollection(SubscriptionPriceModel);

		await prices
			.setRequiredFilter('subscription', subscriptionId)
			.setRequiredFilter('subscriptionPeriod', periodId)
			.list();

		apply(prices.first() || undefined);
	}

	@action
	private reset() {
		this._contractLoadedFor = '';
		this._isContractLoaded = false;
		// Le catalogue porte les tarifs négociés du compte : il est à relire, lui aussi.
		this._isCatalogueLoaded = false;
		this._isPriceLoaded = false;
		this._effectivePrice = undefined;
		this._acknowledgedClosedChangeId = undefined;
		this._hasPickedPeriod = false;
	}

	// ————— Le contrat du compte —————

	public get accountSubscription() {
		const accountUrn = appStore.accountUrn;

		return accountUrn
			? this._accountSubscriptions.findByResolvableUrn('account', accountUrn) || undefined
			: undefined;
	}

	public get isLoaded() {
		return this._isContractLoaded;
	}

	public get billingProfile() {
		return this._billingProfiles.first() || undefined;
	}

	/** Un compte gratuit n'a de factures à consulter que s'il a payé auparavant. */
	public get hasInvoicesToShow() {
		return !isFreePlan(this.accountSubscription?.subscription) || this._invoices.total > 0;
	}

	public get effectivePrice() {
		return this._effectivePrice;
	}

	public get isPriceLoaded() {
		return this._isPriceLoaded;
	}

	/** Échéance à annoncer : reconduction, ou fin d'abonnement si une résiliation est demandée. */
	public get term() {
		const accountSubscription = this.accountSubscription;

		return accountSubscription ? subscriptionTerm(accountSubscription, this.pendingChange) : undefined;
	}

	/** Une facture impayée ne bloque un changement que si l'abonnement vit encore chez le prestataire. */
	public get hasBlockingUnpaidInvoice() {
		return !!this.accountSubscription?.hasUnpaidInvoice
			&& !!this.accountSubscription?.hasPaymentProviderSubscription;
	}

	public get arePlansLoaded() {
		return this._plans.isLoaded;
	}

	/** Le plan gratuit, vers lequel bascule une résiliation. */
	private get freePlan() {
		return this._plans.find(plan => isFreePlan(plan)) || undefined;
	}

	/** Le compte peut-il résilier ? Seulement s'il paie quelque chose. */
	public get isCancellable() {
		return !!this.accountSubscription
			&& !isFreePlan(this.accountSubscription.subscription)
			&& !!this.freePlan;
	}

	public get scheduledCancellationAt() {
		const change = this.pendingChange;

		return change && isFreePlan(change.toSubscription) ? change.scheduledAt : undefined;
	}

	/** Programme le retour au plan gratuit à l'échéance, et rend la date annoncée par le back. */
	public async cancelSubscription() {
		const accountSubscription = this.accountSubscription;
		const freePlan = this.freePlan;

		if (!accountSubscription || !freePlan) return undefined;

		const scheduledAt = await requestSubscriptionCancellation(accountSubscription, freePlan);

		await this.refresh();

		return scheduledAt;
	}

	// ————— Les demandes de changement —————

	/** Demande encore ouverte : en attente d'échéance, ou paiement en cours chez le prestataire. */
	public get pendingChange() {
		const change = this._openChanges.first();

		if (!change?.toSubscription.isLoaded || !change.fromSubscription.isLoaded) return undefined;

		return change;
	}

	/**
	 * Dernière demande que le back a fermée lui-même, faute de paiement. Une demande abandonnée par
	 * le client porte son nom dans `updatedBy` — et le prestataire efface parfois cet auteur, d'où
	 * l'acquittement local posé au moment de l'abandon.
	 */
	public get closedChange() {
		const change = this._closedChanges.first();

		if (!change || !change.toSubscription.isLoaded) return undefined;

		// Une demande à nouveau ouverte remplace l'annonce de la précédente.
		if (this.pendingChange) return undefined;

		if (change.get('updatedBy')) return undefined;

		const closedAt = change.updatedAt;

		if (!closedAt?.isValid() || closedAt.isBefore(dayjs().subtract(ANNOUNCE_CLOSED_FOR_DAYS, 'day'))) {
			return undefined;
		}

		const isRead = change.id === this._acknowledgedClosedChangeId || isClosedChangeAcknowledged(change.id);

		return isRead ? undefined : change;
	}

	@action
	public acknowledgeClosedChange() {
		const change = this._closedChanges.first();

		if (!change) return;

		acknowledgeClosedChange(change.id);
		this._acknowledgedClosedChangeId = change.id;
	}

	// ————— Le catalogue —————

	public get isCatalogueLoaded() {
		return this._isCatalogueLoaded;
	}

	public get periodName() {
		return this._periodName;
	}

	@action
	public setPeriodName(periodName: Account_SubscriptionPeriodName) {
		this._periodName = periodName;
		this._hasPickedPeriod = true;
	}

	/** Périodicité regardée dans la grille. */
	public get period() {
		return this._periods.find(period => period.name === this._periodName) || undefined;
	}

	/** Plans proposés : ceux qui ont un tarif, le gratuit, le sur-mesure, et celui du compte. */
	public get plans() {
		return this._plans.models.filter(plan => (
			isFreePlan(plan)
			|| isOnDemandPlan(plan)
			|| plan.id === this.accountSubscription?.subscription?.id
			|| !!this.pricesOf(plan).length
		));
	}

	/** Tarif d'un plan pour la périodicité regardée : le négocié prime sur le catalogue. */
	public amountOf(plan: SubscriptionModel) {
		return (this.negotiatedPriceOf(plan) || this.catalogPriceOf(plan))?.amountExcludingVat;
	}

	/** Tarif mensuel d'un plan : seule base comparable d'un plan à l'autre. */
	private referenceAmountOf(plan: SubscriptionModel) {
		const monthly = this._periods.find(period => period.name === 'MONTHLY');
		const negotiated = this._negotiatedPrices.find(price => this.isPricedFor(price, plan, monthly?.id));
		const catalog = this._catalogPrices.find(price => this.isPricedFor(price, plan, monthly?.id));

		return (negotiated || catalog)?.amountExcludingVat;
	}

	public negotiatedPriceOf(plan: SubscriptionModel) {
		return this._negotiatedPrices.find(price => this.isPricedFor(price, plan, this.period?.id)) || undefined;
	}

	/** Périodicités dans lesquelles un plan est proposé, pour l'expliquer quand elle ne l'est pas. */
	public periodLabelsOf(plan: SubscriptionModel) {
		return this.pricesOf(plan)
			.map(price => this._periods.find(period => period.id === price.getId('subscriptionPeriod'))?.label)
			.filter((label): label is string => !!label)
			.map(label => label.toLowerCase());
	}

	private catalogPriceOf(plan: SubscriptionModel) {
		return this._catalogPrices.find(price => this.isPricedFor(price, plan, this.period?.id));
	}

	private pricesOf(plan: SubscriptionModel) {
		return [
			...this._catalogPrices.filter(price => price.getId('subscription') === plan.id),
			...this._negotiatedPrices.filter(price => price.getId('subscription') === plan.id),
		];
	}

	private isPricedFor(
		price: SubscriptionPriceModel | AccountSubscriptionPriceModel,
		plan: SubscriptionModel,
		periodId?: id,
	) {
		return price.getId('subscription') === plan.id && price.getId('subscriptionPeriod') === periodId;
	}

	// ————— Ce que vaut un changement de plan —————

	/** Le plan visé est-il celui du compte, dans la périodicité regardée ? */
	public isCurrentOffer(plan: SubscriptionModel) {
		const currentPeriodName = this.accountSubscription?.subscriptionPeriod?.name;

		return plan.id === this.accountSubscription?.subscription?.id
			&& (isFreePlan(plan) || !currentPeriodName || currentPeriodName === this._periodName);
	}

	/**
	 * Le client paiera-t-il davantage ? Sur le même plan, on compare les prix affichés ; d'un plan
	 * à l'autre, leurs prix mensuels, seuls comparables entre périodicités.
	 */
	public isUpgrade(plan: SubscriptionModel) {
		const currentPlan = this.accountSubscription?.subscription;
		const currentAmount = this._effectivePrice?.amountExcludingVat || 0;

		if (plan.id === currentPlan?.id) return (this.amountOf(plan) || 0) > currentAmount;

		const target = this.referenceAmountOf(plan);
		const current = currentPlan && this.referenceAmountOf(currentPlan);

		if (target !== undefined && current !== undefined) return target > current;

		return (this.amountOf(plan) || 0) > currentAmount;
	}

	/**
	 * Le back refuse une demande qui change le plan et la périodicité en même temps. Deux cas y
	 * échappent : souscrire depuis le gratuit (aucune périodicité à conserver) et revenir au
	 * gratuit (ce plan n'en a pas).
	 */
	public changesPlanAndPeriod(plan: SubscriptionModel) {
		const currentPeriodName = this.accountSubscription?.subscriptionPeriod?.name;

		return !isFreePlan(plan)
			&& !!currentPeriodName
			&& currentPeriodName !== this._periodName
			&& plan.id !== this.accountSubscription?.subscription?.id;
	}

	// ————— Les fonctionnalités de l'abonnement —————

	/** Ce que l'abonnement comprend, dans l'ordre où on l'affiche. */
	public get includedFunctionalities() {
		const accountSubscriptionId = this.accountSubscription?.id;

		if (!accountSubscriptionId) return { functionalities: [], isLoaded: false };

		const { isLoaded, values } = functionalityStore.accountValues(accountSubscriptionId);
		const included = values.filter(value => functionalityStore.isIncluded(value.reference, value.value));

		return { functionalities: sortForDisplay(included), isLoaded };
	}

	/** Tout ce que le catalogue propose, groupé par thème, dans un ordre que ne dicte aucune offre. */
	@computed
	public get catalogueFunctionalityGroups() {
		const byReference = new Map<string, FunctionalityValue>();
		const offerCount = new Map<string, number>();

		this.plans.forEach(plan => {
			if (!plan.id || isOnDemandPlan(plan)) return;

			functionalityStore.planValues(plan.id).values.forEach(value => {
				if (!byReference.has(value.reference)) byReference.set(value.reference, value);

				if (functionalityStore.isIncluded(value.reference, value.value)) {
					offerCount.set(value.reference, (offerCount.get(value.reference) ?? 0) + 1);
				}
			});
		});

		const unthemed = new Map(byReference);

		// NOTE: classer par nombre d'offres met les acquis avant les manques dans chaque carte à la
		// fois, tant que les offres restent emboîtées. Deux offres qui se chevaucheraient sans
		// s'emboîter dégraderaient cet ordre sans que rien ne le signale.
		const groups = FUNCTIONALITY_THEMES.map(theme => ({
			functionalities: theme.references.reduce<FunctionalityValue[]>((acc, reference) => {
				const value = unthemed.get(reference);

				if (value) unthemed.delete(reference);

				return value ? [...acc, value] : acc;
			}, []).sort((a, b) => (offerCount.get(b.reference) ?? 0) - (offerCount.get(a.reference) ?? 0)),
			title: theme.title,
		})).filter(group => group.functionalities.length);

		const others = sortForDisplay([...unthemed.values()]);

		return others.length
			? [...groups, { functionalities: others, title: OTHER_FUNCTIONALITIES_TITLE }]
			: groups;
	}

	/** Ce que ce plan couvre, rapporté au catalogue et à l'abonnement en cours. */
	public functionalitiesOf(plan: SubscriptionModel) {
		if (isOnDemandPlan(plan)) return { groups: [], isLoaded: true };

		const { isLoaded, values } = plan.id
			? functionalityStore.planValues(plan.id)
			: { isLoaded: false, values: [] as FunctionalityValue[] };

		const accountSubscriptionId = this.accountSubscription?.id;

		const current = accountSubscriptionId
			? functionalityStore.accountValues(accountSubscriptionId)
			: { isLoaded: false, values: [] as FunctionalityValue[] };

		if (!isLoaded || !current.isLoaded) return { groups: [], isLoaded: false };

		const groups = this.catalogueFunctionalityGroups.map(group => ({
			functionalities: group.functionalities.map(functionality => {
				const value = values.find(candidate => candidate.reference === functionality.reference);
				const currentValue = current.values.find(candidate => candidate.reference === functionality.reference);

				return {
					reference: functionality.reference,
					state: functionalityState(value, currentValue),
					title: functionality.title,
					value: value?.value ?? '',
				};
			}),
			title: group.title,
		}));

		return { groups, isLoaded: true };
	}

	/** Les fonctionnalités de toutes les offres sont-elles connues ? */
	public get areFunctionalitiesLoaded() {
		return this.plans.every(plan => this.functionalitiesOf(plan).isLoaded);
	}

	public get areUpgradeOptionsLoaded() {
		return this.isLoaded && this.isCatalogueLoaded && this.areFunctionalitiesLoaded;
	}

	public get onDemandPlan() {
		return this._plans.models.find(isOnDemandPlan);
	}

	/**
	 * La première offre du catalogue, dans son ordre, qui lèverait le blocage : une feature incluse,
	 * ou une limite au-delà de l'usage. Seules comptent les offres payantes plus chères que celle du
	 * compte et vendues dans la périodicité regardée.
	 */
	public upgradeFor(reference: Account_FunctionalityReference, usage?: number) {
		return this.plans.find(plan => this.isProposableUpgrade(plan) && this.unlocks(plan, reference, usage));
	}

	private isProposableUpgrade(plan: SubscriptionModel) {
		return !isFreePlan(plan)
			&& !isOnDemandPlan(plan)
			&& plan.id !== this.accountSubscription?.subscription?.id
			&& this.amountOf(plan) !== undefined
			&& this.isUpgrade(plan);
	}

	private unlocks(plan: SubscriptionModel, reference: Account_FunctionalityReference, usage?: number) {
		const value = this.planValueOf(plan, reference);

		if (value === undefined || !functionalityStore.isIncluded(reference, value)) return false;

		return usage === undefined || toLimit(value) > usage;
	}

	public planValueOf(plan: SubscriptionModel, reference: Account_FunctionalityReference) {
		return functionalityStore.planValues(plan.id).values.find(candidate => candidate.reference === reference)?.value;
	}

	/** Ce que le compte gagne et perd en passant sur ce plan. */
	public compareWith(plan?: SubscriptionModel) {
		const accountSubscriptionId = this.accountSubscription?.id;

		return functionalityStore.compare(
			accountSubscriptionId
				? functionalityStore.accountValues(accountSubscriptionId)
				: { isLoaded: false, values: [] },
			plan?.id ? functionalityStore.planValues(plan.id) : { isLoaded: false, values: [] },
		);
	}
}

export default new SubscriptionStore();
